import type { CanonicalSearchLoad } from '@numeo/types'
import { AgenticHubPayload, AH_REPLAY_BUFFERED_ACTIONS, AH_SearchAgentOptionsPayload, AH_SearchFormUpdatedPayload, AH_SearchParamsPayload } from '@numeo/types'
import { AppDispatch, RootState } from 'store'
import { addAiHubActiveCall } from 'store/callCenterSlice'
import { showError, showInfo, showWarning } from 'utils/snackbar'
import { audioService } from 'utils/audioService'
import type { AgenticSurface } from './agenticHubSlice'
import {
    addCallToLoad,
    addLoad,
    addLoads,
    applyCallResult,
    addPendingRequest,
    batchUpdateLoads,
    bumpEmailVersion,
    clearLoadStreamingState,
    clearThreadStreamingState,
    completeAutonomousRun,
    finalizeStream,
    incrementLoadUnread,
    removeLoads,
    resolvePendingRequest,
    retractStreamedMessage,
    requestPillCollapse,
    markChatQuestionLive,
    setAnalyzingAnswers,
    setAnalyzingBoard,
    setAnalyzingBoardPhase,
    setScoringStartedAt,
    setScoringProgress,
    setScoringSummaryFlash,
    setIsSending,
    setIsStreaming,
    setLastEventId,
    setLoadStreamingError,
    setLoadStreamingState,
    setLoadSuperseded,
    setStreamingText,
    setStreamingThinking,
    setTabPhase,
    setTabStatus,
    setThreadStreamingState,
    setToolCallResult,
    setToolCallStart,
    startAutonomousRun,
    transitionLoadsToRanking,
    updateLoad,
    updateTabTitle,
    setSearchAgentParams,
    setSearchAgentOptions,
    clearSearchAgentOptions,
    mergeSearchForm,
} from './agenticHubSlice'
import { CANCELLED_ROW_LINGER_MS, isArchivingCancellation } from './live-board'

export function cleanupTabEventCache(tabId: string): void {
    _processedEventIds.delete(tabId)
}

// Playful copy shown in the per-load error pill when the email agent's turn fails with a known
// transient code (currently EMAIL_AGENT_LLM_TIMEOUT — provider stalled). One is picked at random
// per error event.
const EMAIL_AGENT_TIMEOUT_MESSAGES = [
    'The AI got stuck on this one. Your turn — please reply!',
    'The AI ran into a wall. You can handle this — please reply!',
    'The AI could not finish. Please take over and reply!',
    "The AI stopped. You're the expert — please take over!",
    "The AI failed this one. The broker needs a human — that's you!",
]
const randomEmailAgentTimeoutMessage = (): string => EMAIL_AGENT_TIMEOUT_MESSAGES[Math.floor(Math.random() * EMAIL_AGENT_TIMEOUT_MESSAGES.length)]

// AgenticHub actions whose eventId we track as a replay cursor. Sourced from @numeo/types
// so the publisher, server-side buffer writer, and client cursor tracker all share a single
// source of truth (AH_REPLAY_BUFFERED_ACTIONS).

// Event-driven signal for useAgenticHub to re-fetch tab searches
type SearchRefetchListener = (tabId: string) => void
let _searchRefetchListener: SearchRefetchListener | null = null
export function onSearchRefetch(listener: SearchRefetchListener | null): void {
    _searchRefetchListener = listener
}

// Notify hook of streaming activity so it can detect socket connectivity loss
type StreamActivityListener = (tabId: string) => void
let _streamActivityListener: StreamActivityListener | null = null
export function onStreamActivity(listener: StreamActivityListener | null): void {
    _streamActivityListener = listener
}

// Fires when AH_StreamStart arrives — used by the pure-loading flow to refetch
// messages so the user's just-sent message bubble surfaces alongside the agent's
// streaming reply (we don't optimistically inject the user message anymore).
type StreamStartedListener = (tabId: string, lane: AgenticSurface) => void
let _streamStartedListener: StreamStartedListener | null = null
export function onStreamStarted(listener: StreamStartedListener | null): void {
    _streamStartedListener = listener
}

// Fires when the unified scoring pipeline finishes a batch (status='scoring_completed').
// Consumers (LoadsPanel via AgenticHubPage) use this to refetch loads and heal any
// socket updates that were dropped — e.g. when the batch update arrived while the tab
// was throttled in the background.
type PipelineCompletedListener = (tabId: string) => void
let _pipelineCompletedListener: PipelineCompletedListener | null = null
export function onPipelineCompleted(listener: PipelineCompletedListener | null): void {
    _pipelineCompletedListener = listener
}
/** Imperatively fire the same /loads refetch that `scoring_completed` /
 *  `initial_pages_complete` trigger. Used by the poll watcher's 20s safety-net
 *  refetch when background scoring outlasts the `scoring_completed` emit. */
export function firePipelineCompleted(tabId: string): void {
    _pipelineCompletedListener?.(tabId)
}

// Fires when AH_EmailUpdated arrives for a thread whose load is not in the current page of
// tab.loads. Consumer (AgenticHubPage) triggers a page-1 refetch so the badge and sort
// promotion reach the user even when they're viewing page 1 of a long load list.
type OffPageEmailUpdateListener = (tabId: string) => void
let _offPageEmailUpdateListener: OffPageEmailUpdateListener | null = null
export function onOffPageEmailUpdate(listener: OffPageEmailUpdateListener | null): void {
    _offPageEmailUpdateListener = listener
}

function applyLoadUpdate(
    dispatch: AppDispatch,
    tabId: string,
    rowNumber: number,
    status: string | undefined,
    statusReason: string | undefined,
    updatedFields: Record<string, unknown> | undefined
): void {
    const raw = { emailStatus: status, statusReason, ...(updatedFields as Partial<CanonicalSearchLoad>) }
    const updates = Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== undefined)) as Partial<CanonicalSearchLoad>
    if (updatedFields?.deletedAt !== undefined) {
        updates.deletedAt = updatedFields.deletedAt as string | null
    }
    dispatch(updateLoad({ tabId, rowNumber, updates }))
    const newStatus = (updates.emailStatus ?? '').toLowerCase()
    // Remove non-recommended loads from "All" view unless server flagged keepVisible (retained loads stay visible)
    const removableStatuses = new Set(['not_matched', 'duplicate', 'processing_failed'])
    if (removableStatuses.has(newStatus) && !updatedFields?.keepVisible) {
        dispatch(removeLoads({ tabId, rowNumbers: [rowNumber] }))
    }
}

const STREAM_EVENTS = new Set([
    'AH_StreamStart',
    'AH_StreamChunk',
    'AH_StreamRetract',
    'AH_Thinking',
    'AH_StreamEnd',
    'AH_ToolCallStart',
    'AH_ToolCallResult',
    'AH_BatchProcessingStart',
])

// Load-table actions must reach the main switch regardless of source. Email-agent emits
// these with a postingId and the per-load streaming branch handles them; broker-outreach
// emits with source='email' but NO postingId (multi-load thread), so without this whitelist
// they would be dropped by the email-source early-return below before reaching addLoad /
// applyLoadUpdate. Stream/chat-lane events stay dropped — that was the original purpose of
// the early-return.
const LOAD_TABLE_ACTIONS = new Set(['AH_LoadAdded', 'AH_LoadsAdded', 'AH_LoadUpdated', 'AH_LoadsBatchUpdated', 'AH_LoadDeleted'])

// Debug: track stream timing

// Per-tab set of already-processed eventIds for buffered actions (Pub/Sub at-least-once delivery).
// Capped at DEDUP_CACHE_SIZE entries per tab to bound memory. Oldest entries are evicted when full.
const DEDUP_CACHE_SIZE = 200
const _processedEventIds = new Map<string, Set<string>>()

function isAlreadyProcessed(tabId: string, eventId: string): boolean {
    const seen = _processedEventIds.get(tabId)
    return seen?.has(eventId) ?? false
}

function markProcessed(tabId: string, eventId: string): void {
    let seen = _processedEventIds.get(tabId)
    if (!seen) {
        seen = new Set()
        _processedEventIds.set(tabId, seen)
    }
    if (seen.size >= DEDUP_CACHE_SIZE) {
        // Evict oldest entry (first insertion order)
        const first = seen.values().next().value
        if (first !== undefined) seen.delete(first)
    }
    seen.add(eventId)
}

export function handleAgenticHubSocketEvent(dispatch: AppDispatch, message: AgenticHubPayload, eventId?: string, getState?: () => RootState): void {
    const { action, tabId } = message
    // Per-action extra fields (subagent, parentToolCallId, source, postingId, ...) live in the
    // discriminated union variants of AgenticHubPayload but the switch below has to read them
    // for every case. Narrow once at the top via `unknown` so each case can do a flat lookup
    // without re-narrowing on every line. Each access still needs to be type-checked at use.
    const msg = message as unknown as Record<string, unknown>
    // Dispatch Help fan-outs ride the same lane and carry OTHER tenants' tabIds — they belong to the
    // dispatch-help handler alone. Every operator copy is stamped by `emitToOperators`, so drop them
    // here rather than relying on each case's tab lookup happening to miss.
    if (msg.source === 'dispatch-help') return
    // The general agent's turns (ADR-048) ride this lane too; its own handler takes them.
    if (msg.chatVariant === 'general') return
    // Lane discriminator: search-agent emits `lane: 'search'` on every event so its
    // stream activity (thinking, tool calls, AH_StreamChunk/Start/End) gets routed
    // into `tab.searchRuntime` and surfaces in SearchAgentPanel. Chat-agent (the
    // default) doesn't set this field and we fall back to 'chat' → `tab.chatRuntime`
    // which is what ChatPill reads.
    const lane: AgenticSurface = msg.lane === 'search' ? 'search' : 'chat'

    // Idempotency guard for buffered actions: Pub/Sub delivers at-least-once and the replay
    // buffer re-delivers on reconnect. Skip if we've already processed this eventId.
    if (eventId && AH_REPLAY_BUFFERED_ACTIONS.has(action)) {
        if (isAlreadyProcessed(tabId, eventId)) return
        markProcessed(tabId, eventId)
    }

    // Advance the per-tab replay cursor. Only track events that socket-service actually buffers
    // — tracking ephemeral events (e.g. stream chunks) would make the cursor miss real buffered
    // events after disconnect since they were never stored.
    if (eventId && AH_REPLAY_BUFFERED_ACTIONS.has(action)) {
        dispatch(setLastEventId({ tabId, eventId }))
    }

    const isEmailSource = msg.source === 'email'
    const postingId = msg.postingId as string | undefined

    // Track per-load streaming state for email agent events
    // Note: reducer protects approval-pending from being overwritten by thinking/tool-call/StreamEnd
    if (isEmailSource && postingId) {
        switch (action) {
            case 'AH_StreamStart':
            case 'AH_Thinking':
                dispatch(setLoadStreamingState({ tabId, postingId, phase: 'thinking' }))
                break
            case 'AH_StreamChunk':
                dispatch(setLoadStreamingState({ tabId, postingId, phase: 'thinking', text: msg.text as string }))
                break
            case 'AH_ToolCallStart':
                if (message.toolName === 'ask_question') {
                    try {
                        const input = JSON.parse(message.toolInput || '{}') as {
                            title?: string
                            body?: string
                            options?: Array<{ id: string; label: string; description?: string; recommended?: boolean; style?: string }>
                        }
                        const parsedOptions = Array.isArray(input.options)
                            ? input.options
                                  .filter((o) => o && typeof o.id === 'string' && o.id.length > 0)
                                  .map((o) => ({
                                      id: o.id,
                                      label: o.label ?? '',
                                      description: o.description,
                                      recommended: Boolean(o.recommended),
                                      style: o.style,
                                  }))
                            : []
                        if (input.title || parsedOptions.length > 0) {
                            // Add to pendingRequests so it persists until answered/dismissed.
                            // The reducer upserts by id OR by matching resource_keys, so when
                            // AH_RequestCreated arrives with the real requestId it will replace
                            // this entry.
                            dispatch(
                                addPendingRequest({
                                    tabId,
                                    request: {
                                        id: msg.toolCallId as string,
                                        request_type: 'question',
                                        status: 'pending',
                                        surface: 'agent',
                                        title: input.title || '',
                                        body: input.body || '',
                                        options: parsedOptions,
                                        allow_note: true,
                                        resource_keys: [postingId],
                                        created_at: new Date().toISOString(),
                                        resolved_at: null,
                                    },
                                })
                            )
                            dispatch(
                                setLoadStreamingState({
                                    tabId,
                                    postingId,
                                    phase: 'approval-pending',
                                    request: { id: msg.toolCallId as string, title: input.title || '', body: input.body || '', options: parsedOptions },
                                })
                            )
                            break
                        }
                    } catch {
                        /* ignore parse errors */
                    }
                }
                dispatch(setLoadStreamingState({ tabId, postingId, phase: 'tool-call', toolName: message.toolName }))
                break
            case 'AH_RequestCreated': {
                // Update with real requestId from server (overrides toolCallId) so the
                // dispatcher's response lands on the correct request, and ensure the
                // authoritative options/title/body from the server replace any optimistic
                // entry added during AH_ToolCallStart.
                const reqOptions = (Array.isArray(msg.options) ? msg.options : []) as Array<{
                    id: string
                    label: string
                    description?: string
                    recommended?: boolean
                    style?: string
                }>
                // resourceKeys may be sent explicitly (broker-outreach: multi-load) or fall back
                // to [postingId] (email-agent: single-load). multi_select likewise read from the
                // payload — broker-outreach uses true for the load-selection question.
                const payloadResourceKeys = Array.isArray(msg.resourceKeys) ? (msg.resourceKeys as string[]) : null
                const requestResourceKeys = payloadResourceKeys && payloadResourceKeys.length > 0 ? payloadResourceKeys : postingId ? [postingId] : []
                dispatch(
                    addPendingRequest({
                        tabId,
                        request: {
                            id: msg.requestId as string,
                            request_type: (msg.requestType as 'question' | 'approval') ?? 'question',
                            status: 'pending',
                            surface: 'agent',
                            title: (msg.title as string) ?? '',
                            body: (msg.body as string) ?? '',
                            options: reqOptions.map((o) => ({
                                id: o.id,
                                label: o.label,
                                description: o.description,
                                recommended: o.recommended,
                                style: o.style,
                            })),
                            allow_note: msg.allowNote === undefined ? true : Boolean(msg.allowNote),
                            multi_select: Boolean(msg.multiSelect),
                            resource_keys: requestResourceKeys,
                            created_at: new Date().toISOString(),
                            resolved_at: null,
                            metadata_type: typeof msg.metadataType === 'string' ? msg.metadataType : undefined,
                            correlation_id: typeof msg.correlationId === 'string' ? msg.correlationId : undefined,
                            question_index: typeof msg.questionIndex === 'number' ? msg.questionIndex : undefined,
                            questions_total: typeof msg.questionsTotal === 'number' ? msg.questionsTotal : undefined,
                        },
                    })
                )
                dispatch(
                    setLoadStreamingState({
                        tabId,
                        postingId,
                        phase: 'approval-pending',
                        request: {
                            id: msg.requestId as string,
                            title: msg.title as string,
                            body: msg.body as string,
                            options: reqOptions,
                        },
                    })
                )
                break
            }
            case 'AH_LoadUpdated':
                applyLoadUpdate(
                    dispatch,
                    tabId,
                    message.rowNumber,
                    message.status,
                    message.statusReason,
                    message.updatedFields as Record<string, unknown> | undefined
                )
                break
            case 'AH_RequestDismissed':
                // Force clear — use clearLoadStreamingState to bypass reducer protection
                dispatch(clearLoadStreamingState({ tabId, postingId }))
                break
            case 'AH_StreamEnd':
                dispatch(setLoadStreamingState({ tabId, postingId, phase: 'done', finalContent: msg.finalContent as string }))
                break
            case 'AH_Superseded': {
                // The dispatcher kicked off a new search while this load's run was in flight.
                // Mark the row with a friendly "AI stopped" pill (distinct from the red "AI error"
                // pill). Cleared implicitly when the next AH_StreamStart for this posting fires.
                dispatch(setLoadSuperseded({ tabId, postingId }))
                break
            }
            case 'AH_Error': {
                // ABORTED on a per-load surface = generic abort (tab-close today). Supersede uses
                // AH_Superseded above. Clearing here keeps the row clean — tab-close deletes the
                // tab anyway, no UI lives long enough to render anything.
                if (msg.code === 'ABORTED') {
                    dispatch(clearLoadStreamingState({ tabId, postingId }))
                    break
                }
                // Per-load surface: keep the load row in the streaming map but flip phase to 'done'
                // and store the error message. The render layer shows an error pill on that specific
                // row (instead of a global snackbar that doesn't say which load failed).
                const isTimeout = msg.code === 'EMAIL_AGENT_LLM_TIMEOUT'
                const tooltip = isTimeout ? randomEmailAgentTimeoutMessage() : (msg.message as string) || 'Email AI failed'
                dispatch(setLoadStreamingError({ tabId, postingId, message: tooltip }))
                break
            }
        }
    }

    // Broker-outreach equivalent of the per-load streaming branch above. The agent emits with
    // source='email' but no postingId (multi-load thread), so we scope by `threadId` instead and
    // the render layer joins to load rows by `email_thread_id`. We deliberately do NOT handle
    // AH_RequestCreated/AH_ToolCallStart-as-question here — broker-outreach questions surface
    // through `pendingRequests` (already wired) and showing them per-row would conflict with the
    // existing question banner.
    const threadId = msg.threadId as string | undefined
    if (isEmailSource && threadId && !postingId) {
        switch (action) {
            case 'AH_StreamStart':
            case 'AH_Thinking':
                dispatch(setThreadStreamingState({ tabId, threadId, phase: 'thinking' }))
                break
            case 'AH_StreamChunk':
                dispatch(setThreadStreamingState({ tabId, threadId, phase: 'thinking', text: msg.text as string }))
                break
            case 'AH_ToolCallStart':
                dispatch(setThreadStreamingState({ tabId, threadId, phase: 'tool-call', toolName: message.toolName }))
                break
            case 'AH_StreamEnd':
            case 'AH_Error':
            case 'AH_Superseded':
                dispatch(clearThreadStreamingState({ tabId, threadId }))
                break
        }
    }

    // Signal stream activity to the watchdog timer
    if (STREAM_EVENTS.has(action)) {
        _streamActivityListener?.(tabId)
    }

    // Email source events are handled by loadStreamingStates above — skip chat panel dispatches.
    // Exception: load-table actions (LOAD_TABLE_ACTIONS) must fall through so broker-outreach,
    // which emits with source='email' but no postingId, can still mutate tab.loads in real time.
    if (isEmailSource) {
        // Still handle stream settled for refreshing requests
        if (!LOAD_TABLE_ACTIONS.has(action)) return
    }

    // Streaming events must have an explicit source='chat' or source='search' to reach the
    // panel switch. Untagged stream events (e.g. the legacy load-search pipeline's AH_StreamEnd)
    // would otherwise fall through and dispatch into the chat lane, clearing its streaming
    // state unexpectedly. The search-agent runner emits with source='search' + lane='search',
    // so those are routed into searchRuntime by the switch below.
    const isChatSource = msg.source === 'chat'
    const isSearchSource = msg.source === 'search'
    if (STREAM_EVENTS.has(action) && !isChatSource && !isSearchSource) {
        return
    }

    switch (action) {
        case 'AH_StreamStart':
            dispatch(setIsStreaming({ tabId, lane, isStreaming: true, messageId: message.messageId }))
            dispatch(setTabPhase({ tabId, phase: 'chatting' }))
            // Drop any present_options chips from the previous turn so they don't render
            // under the next agent message. The chips are per-turn ephemeral; without this,
            // a turn where the agent doesn't call present_options would still show the
            // prior turn's chips because the slice never clears the field on its own.
            if (lane === 'search') dispatch(clearSearchAgentOptions({ tabId }))
            // Pure-loading model: we no longer optimistically inject the user's message.
            // Trigger a per-lane message refetch so the persisted user-message row + any
            // earlier agent context appears alongside the streaming reply.
            _streamStartedListener?.(tabId, lane)
            break

        case 'AH_StreamChunk':
            // The runner tags subagent chunks with `subagent` + `parentToolCallId`. The slice
            // routes them as a `reply` entry on the parent task card's `events` list (visible
            // inside the task body, in emission order) so analyst narration shows up in the
            // dispatcher UI without leaking into the parent's main reply.
            dispatch(
                setStreamingText({
                    tabId,
                    lane,
                    text: message.text,
                    subagent: msg.subagent as string | undefined,
                    parentToolCallId: msg.parentToolCallId as string | undefined,
                    messageId: message.messageId,
                })
            )
            break

        case 'AH_StreamRetract':
            // The server abandoned this streamed message (repetition-loop trip → retried under a
            // new id). Excise its text segments from the live pane so the degraded text neither
            // renders next to the retried reply nor gets committed by finalizeStream's replay.
            dispatch(retractStreamedMessage({ tabId, lane, messageId: message.messageId }))
            break

        case 'AH_Thinking':
            // Same subagent routing as AH_StreamChunk: subagent reasoning lands as a streaming
            // `thinking` entry on the parent task card's `events` list (in emission order
            // alongside the analyst's tool calls). Parent reasoning still goes to the lane pane.
            dispatch(
                setStreamingThinking({
                    tabId,
                    lane,
                    text: message.text,
                    subagent: msg.subagent as string | undefined,
                    parentToolCallId: msg.parentToolCallId as string | undefined,
                })
            )
            break

        case 'AH_StreamEnd':
            dispatch(
                finalizeStream({
                    tabId,
                    lane,
                    messageId: message.messageId,
                    finalContent: message.finalContent,
                    inputTokens: message.totalInputTokens,
                    outputTokens: message.totalOutputTokens,
                })
            )
            break

        case 'AH_ToolCallStart':
            // `subagent` and `parentToolCallId` are set by the runner for tool calls that
            // happen inside a subagent invocation (e.g. load-analyst). The slice nests them
            // under the parent's `task` card by appending a `tool` entry to its ordered
            // `events` list instead of pushing to the top-level `streamingEventLog`.
            dispatch(
                setToolCallStart({
                    tabId,
                    lane,
                    toolCallId: message.toolCallId,
                    toolName: message.toolName,
                    toolInput: message.toolInput,
                    subagent: msg.subagent as string | undefined,
                    parentToolCallId: msg.parentToolCallId as string | undefined,
                })
            )
            break

        case 'AH_ToolCallResult':
            dispatch(
                setToolCallResult({
                    tabId,
                    lane,
                    toolCallId: message.toolCallId,
                    toolOutput: message.toolOutput ?? '',
                    success: message.success,
                })
            )
            // When search_loads completes, signal to re-fetch tab searches for the watcher
            if (message.toolName === 'search_loads' && message.success) {
                _searchRefetchListener?.(tabId)
            }
            break

        case 'AH_LoadAdded':
            dispatch(addLoad({ tabId, load: message.load }))
            break

        case 'AH_LoadsAdded':
            dispatch(addLoads({ tabId, loads: message.loads }))
            break

        case 'AH_LoadUpdated':
            applyLoadUpdate(
                dispatch,
                tabId,
                message.rowNumber,
                message.status,
                message.statusReason,
                message.updatedFields as Record<string, unknown> | undefined
            )
            break

        case 'AH_LoadsBatchUpdated': {
            const removableStatuses = new Set(['not_matched', 'duplicate', 'processing_failed'])
            const batchedUpdates: Array<{ rowNumber: number; updates: Partial<CanonicalSearchLoad> }> = []
            const removeRowNumbers: number[] = []
            // A load the broker took off the board arrives archived. Its row stays on the board here,
            // reading "Cancelled", until the linger is up — then it moves to Archived.
            const lingeringArchives: Array<{ rowNumber: number; updates: Partial<CanonicalSearchLoad> }> = []
            for (const u of message.updates) {
                const raw = { emailStatus: u.status, statusReason: u.statusReason, ...(u.updatedFields as Partial<CanonicalSearchLoad>) }
                const updates = Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== undefined)) as Partial<CanonicalSearchLoad>
                if ((u.updatedFields as Record<string, unknown> | undefined)?.deletedAt !== undefined) {
                    updates.deletedAt = (u.updatedFields as Record<string, unknown>).deletedAt as string | null
                }
                if (isArchivingCancellation(updates)) {
                    lingeringArchives.push({ rowNumber: u.rowNumber, updates: { deletedAt: updates.deletedAt } })
                    delete updates.deletedAt
                }
                batchedUpdates.push({ rowNumber: u.rowNumber, updates })
                const newStatus = (updates.emailStatus ?? '').toLowerCase()
                if (removableStatuses.has(newStatus) && !(u.updatedFields as Record<string, unknown> | undefined)?.keepVisible) {
                    removeRowNumbers.push(u.rowNumber)
                }
            }
            dispatch(batchUpdateLoads({ tabId, updates: batchedUpdates, removeRowNumbers }))
            if (lingeringArchives.length > 0) {
                setTimeout(() => dispatch(batchUpdateLoads({ tabId, updates: lingeringArchives, removeRowNumbers: [] })), CANCELLED_ROW_LINGER_MS)
            }
            break
        }

        case 'AH_AutonomousRun':
            if (message.status === 'running') {
                dispatch(
                    startAutonomousRun({
                        tabId,
                        run: {
                            runId: message.runId,
                            kind: message.kind,
                            status: 'running',
                            label: message.label,
                            stats: message.stats,
                            startedAt: Date.now(),
                        },
                    })
                )
            } else {
                dispatch(
                    completeAutonomousRun({
                        tabId,
                        runId: message.runId,
                        status: message.status,
                        label: message.label,
                        errorMessage: message.errorMessage,
                    })
                )
            }
            break

        case 'AH_ContextCompacting':
            if (message.status === 'started') {
                showInfo('Compacting conversation history...', 10000)
            } else if (message.status === 'completed') {
                showInfo(message.message || 'Context compaction completed', 3000)
            }
            break

        case 'AH_SearchPipelineStatus': {
            if (message.status === 'ranking_started' || message.status === 'scoring_started') {
                dispatch(transitionLoadsToRanking({ tabId }))
                // NOTE: under v2 the search pipeline emits `ranking_started` +
                // `scoring_completed` even though no scoring runs (real
                // scoring happens later via the chat `scoring` tool). Stamping
                // `scoringStartedAt`/`scoringProgress` here used to flash a
                // false-positive "Scoring loads with AI…" overlay during
                // every search. Now we stamp lazily on the first real
                // `scoring_progress` event — only the tool emits those, so
                // the overlay only appears when scoring actually runs.
                // Under v2, collapse the ChatPill for the active tab so the
                // scoring overlay (which covers the table) isn't hidden
                // behind the expanded chat card.
                const v2 = getState ? !!getState().agenticHub.searchFlowExperimentsV2Enabled : false
                const activeTabId = getState ? getState().agenticHub.activeTabId : null
                if (v2 && activeTabId && activeTabId === tabId) {
                    dispatch(requestPillCollapse())
                }
            } else if (message.status === 'scoring_progress') {
                const total = typeof message.total === 'number' ? message.total : 0
                const processed = typeof message.processed === 'number' ? message.processed : 0
                // Lazy stamp — first real progress event from the scoring tool
                // marks scoring-active (drives the non-blocking glow ring + the 30s
                // refetch loop). Pipeline-side ranking_started never gets here
                // (pipeline doesn't emit progress under v2).
                const currentStartedAt = getState ? getState().agenticHub.tabs[tabId]?.scoringStartedAt : null
                if (!currentStartedAt) {
                    dispatch(setScoringStartedAt({ tabId, scoringStartedAt: new Date().toISOString() }))
                }
                dispatch(setScoringProgress({ tabId, progress: { processed, total } }))
                // Each batch milestone (BE flushes + emits every 20 groups; plus the
                // initial 0/total tick) refetches /loads so the table fills in
                // progressively — rows flip from `processing` to their scored status
                // as the BE persists them. Complements the 30s scoring poll.
                //
                // Gated on the new-search-flow flag: the LEGACY v1 scorer
                // (`src/search-pipeline/v2/scoring-{one-shot,progressive}`) ALSO emits
                // `scoring_progress`, so without this gate a v1 search (both flags
                // off) would spin up an extra refetch loop. The flag is on only when
                // the chat `scoring` tool — the source of THIS progressive flow — runs.
                const scoringFlowV2 = getState ? !!getState().agenticHub.searchFlowExperimentsV2Enabled : false
                if (scoringFlowV2) _pipelineCompletedListener?.(tabId)
            } else if (message.status === 'all_sources_complete') {
                // Under v1: pagination done = pill done → clear.
                // Under v2: pagination done = flip from "Processing loads" (with progress ring) to
                // "Analyzing board" (dots-only, indeterminate). The pill stays through the
                // post-search autonomous run; `autonomous_run_complete` does the final clear.
                const v2 = getState ? !!getState().agenticHub.searchFlowExperimentsV2Enabled : false
                if (v2) {
                    dispatch(setAnalyzingBoardPhase({ tabId, phase: 'analyzing' }))
                } else {
                    dispatch(setAnalyzingBoard({ tabId, analyzingBoard: null }))
                }
            } else if (message.status === 'autonomous_run_complete') {
                // Fires after EITHER an initial-search autonomous run OR an
                // answer-triggered run from `maybeResolveChatAgentQuestionGroup`.
                // Both clear the analyzingBoard loader (v2 only) and the
                // "Analyzing answers" pill copy. v1 doesn't render those, so
                // the dispatches are no-ops in that path.
                const v2 = getState ? !!getState().agenticHub.searchFlowExperimentsV2Enabled : false
                if (v2) {
                    dispatch(setAnalyzingBoard({ tabId, analyzingBoard: null }))
                }
                dispatch(setAnalyzingAnswers({ tabId, value: false }))
            } else if (message.status === 'scoring_completed') {
                // Heal any socket updates that arrived while the tab was backgrounded —
                // refetch loads from DB so the UI matches the canonical state.
                _pipelineCompletedListener?.(tabId)
                dispatch(setTabPhase({ tabId, phase: 'results' }))
                dispatch(setScoringStartedAt({ tabId, scoringStartedAt: null }))
                dispatch(setScoringProgress({ tabId, progress: null }))
                // Tier counts ride the completion event (structured fields, not just
                // the message string). Stamp them so the collapsed chat pill can flash
                // "N recommended, N matched, …". ChatPill gates this to the idle pill.
                // Flash is root-level (one pill, one active tab) — only stamp it for the
                // tab the user is actually viewing, so a background tab's poll/scoring
                // completion doesn't flash another tab's counts on the active pill.
                const activeTabId = getState ? getState().agenticHub.activeTabId : null
                const recommended = typeof message.recommended === 'number' ? message.recommended : 0
                const matched = typeof message.matched === 'number' ? message.matched : 0
                const potential = typeof message.potential === 'number' ? message.potential : 0
                const deduplicated = typeof message.deduplicated === 'number' ? message.deduplicated : 0
                if (activeTabId === tabId && recommended + matched + potential > 0) {
                    dispatch(setScoringSummaryFlash({ recommended, matched, potential, deduplicated }))
                }
            } else if (message.phase === 'sources-partial') {
                // Surface failed sources from the unified search path so the dispatcher
                // knows why some loadboards returned nothing. The inline banner consumes
                // this event and groups failures by reason. Non-blocking to the pipeline.
                const rawFailed = Array.isArray(message.failedSources) ? message.failedSources : []
                // Backend may send either string[] (legacy) or { source, reason }[] (current).
                // Normalize to the current shape for the banner.
                const failed = rawFailed
                    .map((entry: unknown) => {
                        if (typeof entry === 'string') return { source: entry, reason: 'unknown', error: undefined }
                        const e = entry as { source?: unknown; reason?: unknown; error?: unknown }
                        return {
                            source: String(e.source ?? ''),
                            reason: typeof e.reason === 'string' ? e.reason : 'unknown',
                            error: typeof e.error === 'string' ? e.error : undefined,
                        }
                    })
                    .filter((e) => e.source)
                const succeeded = Array.isArray(message.succeededSources) ? (message.succeededSources as string[]) : []
                const disconnected = Array.isArray(message.disconnected) ? (message.disconnected as string[]) : []
                if (failed.length > 0) {
                    // Banner-only event. State sync (flipping pills to disconnected) is handled
                    // separately via `agentic-hub:source-disconnected` so consumers can opt into
                    // each concern independently.
                    window.dispatchEvent(new CustomEvent('agentic-hub:sources-partial', { detail: { tabId, failed, succeeded } }))
                }
                if (disconnected.length > 0) {
                    window.dispatchEvent(new CustomEvent('agentic-hub:source-disconnected', { detail: { tabId, sources: disconnected } }))
                }
            }
            break
        }

        case 'AH_LoadDeleted':
            // Bridge may send rowNumber (singular) or rowNumbers (array)
            dispatch(removeLoads({ tabId, rowNumbers: message.rowNumbers ?? (message.rowNumber != null ? [message.rowNumber] : []) }))
            break

        case 'AH_ContextUpdated':
            // contextSize / input / output token counts aren't surfaced in the hub (the classic
            // header chip that read them was retired). The `status` transition, however,
            // gates useAutonomousWakeup's fully-autonomous mode — keep that side effect.
            if (message.status === 'shutdown' || message.status === 'enabled') {
                dispatch(setTabStatus({ tabId, status: message.status }))
            }
            break

        case 'AH_TabTitleUpdate':
            dispatch(updateTabTitle({ tabId, text: message.text, done: message.done }))
            break

        case 'AH_CallStarted':
            // Both gated on postingId: the only consumer of aiHubActiveCalls is
            // useAgenticHubCall, which is mounted per-load. Phone-only calls have no UI surface.
            // AH_* events are AI-Hub-only by channel definition (only GAS emits them) so no
            // metadata.source discrimination is needed here.
            if (message.postingId) {
                dispatch(addCallToLoad({ tabId, postingId: message.postingId, callId: message.callId }))
                dispatch(addAiHubActiveCall(message.call))
            }
            break

        case 'AH_CallCompleted':
            // The CallStatus.ENDED transition itself rides on CC_CallEnded (callCenter slice).
            // This branch persists the result summary + structured outputs onto the load row so
            // CallHistorySection renders the "Summary:" line and the row's rate/commodity/etc.
            // reflect the call without waiting for a refetch.
            if (message.postingId) {
                dispatch(
                    applyCallResult({
                        tabId,
                        postingId: message.postingId,
                        resultText: message.resultText,
                        resultAt: message.resultAt,
                        collectedInfo: message.collectedInfo,
                    })
                )
            }
            break

        case 'AH_EmailUpdated':
            if (message.emailThreadId) {
                if (message.newEmailFromBroker) dispatch(incrementLoadUnread({ tabId, emailThreadId: message.emailThreadId }))
                // Only bump email version (which triggers EmailConversation refetch) for broker replies,
                // not for our own sends — avoids collapsing the optimistic bubble prematurely.
                if (message.newEmailFromBroker) dispatch(bumpEmailVersion({ tabId, emailThreadId: message.emailThreadId }))
                // If the load is on a page that hasn't been loaded yet, the reducers above are no-ops.
                // Fire the listener so AgenticHubPage can trigger a page-1 refetch to surface the badge.
                // Skip for broker-outreach inquiry threads — those don't appear in tab.loads at all
                // and the inquiry-keyed bump in bumpEmailVersion already handled the refetch.
                if (getState) {
                    const state = getState().agenticHub
                    const tab = state.tabs[tabId]
                    const loadInPage = tab?.loads.some((l) => l.emailThreadId === message.emailThreadId)
                    const isInquiryThread = state.activeBrokerInquiry?.threadId === message.emailThreadId
                    if (!loadInPage && !isInquiryThread) _offPageEmailUpdateListener?.(tabId)
                } else {
                    console.warn('[AH-Socket] AH_EmailUpdated: getState not provided — off-page detection skipped')
                }
            }
            break

        case 'AH_RequestCreated': {
            const reqMsg = msg as Record<string, unknown>
            const reqOptions = (Array.isArray(reqMsg.options) ? reqMsg.options : []) as Array<{
                id: string
                label: string
                description?: string
                recommended?: boolean
                style?: string
            }>
            // resourceKeys: explicit when broker-outreach (multi-load); fall back to [postingId] for email-agent.
            const payloadResourceKeys = Array.isArray(reqMsg.resourceKeys) ? (reqMsg.resourceKeys as string[]) : null
            const requestResourceKeys = payloadResourceKeys && payloadResourceKeys.length > 0 ? payloadResourceKeys : postingId ? [postingId] : []
            // Mark chat-agent question batches as "freshly arrived from socket"
            // so the ChatPill auto-popup fires only for this live emit, not for
            // batches rehydrated from `/requests` on page refresh. Other request
            // types (broker-outreach, email-agent) don't trigger the popup so
            // gating is scoped to the chat-agent metadata discriminator.
            const incomingMetadataType = typeof reqMsg.metadataType === 'string' ? (reqMsg.metadataType as string) : undefined
            const incomingCorrelationId = typeof reqMsg.correlationId === 'string' ? (reqMsg.correlationId as string) : undefined
            if (incomingMetadataType === 'chat_agent_questions' && incomingCorrelationId) {
                dispatch(markChatQuestionLive({ tabId, correlationId: incomingCorrelationId }))
                // Audible ping — chat-agent questions block the run until answered, so
                // the dispatcher must not miss them. Throttled + user-interaction-gated
                // inside the service; play failures (autoplay policy) are non-fatal.
                audioService.playSound().catch(() => {})
            }
            dispatch(
                addPendingRequest({
                    tabId,
                    request: {
                        id: String(reqMsg.requestId ?? ''),
                        request_type: (reqMsg.requestType as 'question' | 'approval') ?? 'question',
                        status: 'pending',
                        surface: isEmailSource ? 'agent' : 'chat',
                        title: String(reqMsg.title ?? ''),
                        body: String(reqMsg.body ?? ''),
                        options: reqOptions.map((o) => ({ id: o.id, label: o.label, description: o.description, recommended: o.recommended, style: o.style })),
                        // Match the other branch (line 315) and the parse-requests
                        // hydrate path: undefined → true so the "Other" free-text
                        // affordance renders. Senders must explicitly pass `false`
                        // to disable it.
                        allow_note: reqMsg.allowNote === undefined ? true : Boolean(reqMsg.allowNote),
                        // multi_select: question-first flow's load-selection question (broker-outreach v2).
                        // allow_edit: email approvals where the dispatcher can tweak the draft inline before sending.
                        // Independent flags from independent feature lines — both surface to LoadQuestionCard.
                        multi_select: Boolean(reqMsg.multiSelect),
                        allow_edit: Boolean(reqMsg.allowEdit),
                        resource_keys: requestResourceKeys,
                        created_at: new Date().toISOString(),
                        resolved_at: null,
                        metadata_type: typeof reqMsg.metadataType === 'string' ? (reqMsg.metadataType as string) : undefined,
                        correlation_id: typeof reqMsg.correlationId === 'string' ? (reqMsg.correlationId as string) : undefined,
                        question_index: typeof reqMsg.questionIndex === 'number' ? (reqMsg.questionIndex as number) : undefined,
                        questions_total: typeof reqMsg.questionsTotal === 'number' ? (reqMsg.questionsTotal as number) : undefined,
                    },
                })
            )
            break
        }

        case 'AH_RequestDismissed':
            if (message.requestId) {
                dispatch(resolvePendingRequest({ tabId, requestId: message.requestId }))
            }
            // Also clear load streaming state if email source
            if (isEmailSource && postingId) {
                dispatch(setLoadStreamingState({ tabId, postingId, phase: 'done' }))
            }
            break

        case 'AH_FactoringNotConnected':
            showWarning(`${message.providerLabel || 'Factoring provider'} is not connected. Connect in Settings or switch provider to see broker scores.`, 8000)
            break

        case 'AH_SearchParams': {
            const params = (message as AH_SearchParamsPayload).params
            if (params) dispatch(setSearchAgentParams({ tabId, params }))
            break
        }

        case 'AH_SearchFormUpdated': {
            // Chat-agent `update_dispatcher_filter` wrote the tab's search_form. Shallow-merge
            // the (FE-shaped) patch into the tab's form, drop whatever it cleared, and bump the
            // sync nonce so the mounted form re-hydrates its inputs.
            const { patch, cleared } = message as AH_SearchFormUpdatedPayload
            if ((patch && Object.keys(patch).length > 0) || cleared?.length) dispatch(mergeSearchForm({ tabId, patch: patch ?? {}, cleared }))
            break
        }

        case 'AH_SearchAgentOptions': {
            const payload = message as AH_SearchAgentOptionsPayload
            if (payload.messageId && Array.isArray(payload.options) && payload.options.length > 0) {
                dispatch(
                    setSearchAgentOptions({
                        tabId,
                        messageId: payload.messageId,
                        title: payload.title,
                        body: payload.body,
                        options: payload.options,
                        multiSelect: payload.multiSelect,
                    })
                )
            }
            break
        }

        case 'AH_Superseded':
            // Chat/search/deepagent run cancelled by the dispatcher's new search. Finalize the
            // in-flight stream so any partial thinking/tool-call messages are preserved, but skip
            // the error toast — the cancellation was intentional, not a failure.
            dispatch(setIsSending({ tabId, lane, isSending: false }))
            dispatch(finalizeStream({ tabId, lane, messageId: `superseded-${Date.now()}`, finalContent: '' }))
            break

        case 'AH_Error':
            // Finalize any in-flight streaming events so tool calls/thinking are preserved as messages
            dispatch(setIsSending({ tabId, lane, isSending: false }))
            dispatch(finalizeStream({ tabId, lane, messageId: `error-${Date.now()}`, finalContent: '' }))
            // ABORTED = generic abort (tab-close today). Supersede now uses AH_Superseded above.
            // Either way, no error toast for user-initiated cancellations.
            if (msg.code !== 'ABORTED') {
                showError(message.message || 'An error occurred')
            }
            break
    }
}
